import React, { useEffect, useRef } from "react";
import { glyphFor, venueNames } from "./safeSpotTokens";
import { markerAriaLabel, markerState } from "./safeSpotOpenState";
import "./safe-spot.css";

/**
 * Events already handled by this pin, so React's delegated click (jsdom) and
 * the native listener (Mapbox portal) cannot both fire the select.
 */
const handled = new WeakSet();

export const VenueGlyph = ({ category, className = "ss-glyph" }) => (
  <svg className={className} viewBox="0 0 24 24" aria-hidden="true">
    {glyphFor(category).map((d) => (
      <path key={d} d={d} />
    ))}
  </svg>
);

/**
 * The spec marker: a sage rounded tile on a notch, never a teardrop.
 *
 * Mapbox Marker children do not receive React's delegated click in this stack
 * (see MapView.attachActivate). The listener is native, and it stopPropagates
 * so the same tap cannot also count as a map-background dismiss.
 */
const SafeSpotMarker = ({
  spot,
  selected = false,
  showLabel = false,
  lang = "en",
  onSelect,
}) => {
  const rootRef = useRef(null);
  const { primary } = venueNames(spot, lang);
  const state = markerState(spot);
  const closed = state === "closed";

  useEffect(() => {
    const node = rootRef.current;
    if (!node) return undefined;
    const activate = (evt) => {
      if (typeof evt.stopPropagation === "function") evt.stopPropagation();
      if (handled.has(evt)) return;
      handled.add(evt);
      onSelect?.(spot);
    };
    node.addEventListener("click", activate);
    return () => node.removeEventListener("click", activate);
  }, [onSelect, spot]);

  return (
    <button
      ref={rootRef}
      type="button"
      className={"ss-marker" + (selected ? " is-selected" : "")}
      data-state={state}
      data-testid={"safe-spot-" + spot._id}
      aria-label={markerAriaLabel(spot, primary)}
      aria-pressed={selected}
    >
      <span className="ss-tile">
        <VenueGlyph category={spot.category} />
      </span>
      <span className="ss-notch" aria-hidden="true" />
      {showLabel && primary ? (
        <span className="ss-label">
          <span className="ss-dot" aria-hidden="true" />
          <span className="ss-name">{primary}</span>
          {closed ? <span className="ss-closed-word">· Closed</span> : null}
        </span>
      ) : null}
    </button>
  );
};

export default SafeSpotMarker;
